import type {
  AdminGoalResponse,
  AdminUserResponse,
  CreateEpisodeBody,
  CreateGoalBody,
  CreateJourneyBody,
  CreateJourneyDayBody,
  CreateSectionBody,
  CreateSubJourneyBody,
  EpisodeResponse,
  FeedbackPage,
  JourneyDayResponse,
  JourneyResponse,
  ListUsersQuery,
  Page,
  PublishCheckResponse,
  SectionResponse,
  SubJourneyResponse,
  UpdateEpisodeBody,
  UpdateGoalBody,
  UpdateJourneyBody,
  UpdateSectionBody,
  UpdateSubJourneyBody,
  WaitlistPage,
} from '@manasil/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';

/** One place to name every cache key, so invalidation is never guesswork. */
export const keys = {
  goals: ['goals'] as const,
  journeys: (published?: boolean) => ['journeys', published ?? 'all'] as const,
  users: (search: string, onboarded?: boolean) => ['users', search, onboarded ?? 'all'] as const,
  journey: (id: string) => ['journey', id] as const,
  days: (id: string) => ['journey', id, 'days'] as const,
  publishCheck: (id: string) => ['journey', id, 'publish-check'] as const,
  sections: (id: string) => ['journey', id, 'sections'] as const,
  subJourneys: (id: string) => ['journey', id, 'sub-journeys'] as const,
  subJourney: (subJourneyId: string) => ['sub-journey', subJourneyId] as const,
  episodes: (subJourneyId: string) => ['sub-journey', subJourneyId, 'episodes'] as const,
  waitlist: (search: string, purpose?: string) => ['waitlist', search, purpose ?? 'all'] as const,
  feedback: (journeyId?: string, rating?: number) =>
    ['feedback', journeyId ?? 'all', rating ?? 'all'] as const,
};

/**
 * Website signups. Deliberately the first page only: this screen is for reading what just
 * came in, and walking a cursor belongs behind an export button rather than an infinite
 * scroll nobody asked for. `total` rides along with the page, so the count stays honest
 * even though the list is not the whole list.
 */
export function useWaitlist(input: { search: string; purpose?: string }) {
  const params = new URLSearchParams({ limit: '50' });
  if (input.search.trim()) params.set('search', input.search.trim());
  if (input.purpose) params.set('purpose', input.purpose);
  return useQuery({
    queryKey: keys.waitlist(input.search, input.purpose),
    queryFn: () => api<WaitlistPage>(`/admin/waitlist?${params.toString()}`),
    staleTime: 15_000,
  });
}

/** The feedback inbox: first page only, newest first, like the waitlist. */
export function useFeedback(input: { journeyId?: string; rating?: number }) {
  const params = new URLSearchParams({ limit: '50' });
  if (input.journeyId) params.set('journeyId', input.journeyId);
  if (input.rating) params.set('rating', String(input.rating));
  return useQuery({
    queryKey: keys.feedback(input.journeyId, input.rating),
    queryFn: () => api<FeedbackPage>(`/admin/feedback?${params.toString()}`),
    staleTime: 15_000,
  });
}

/** Every goal, including retired ones — the goals screen needs those too. */
export function useGoals() {
  return useQuery({
    queryKey: keys.goals,
    queryFn: () => api<{ items: AdminGoalResponse[] }>('/admin/goals').then((r) => r.items),
    staleTime: 60_000,
  });
}

export function useCreateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateGoalBody) =>
      api<AdminGoalResponse>('/admin/goals', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.goals }),
  });
}

export function useUpdateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string } & UpdateGoalBody) => {
      const { key, ...body } = input;
      return api<AdminGoalResponse>(`/admin/goals/${key}`, { method: 'PATCH', body });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.goals }),
  });
}

export function useReorderGoals() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (goalKeys: string[]) =>
      api<{ items: AdminGoalResponse[] }>('/admin/goals/reorder', {
        method: 'POST',
        body: { keys: goalKeys },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.goals }),
  });
}

interface UserListResult extends Page<AdminUserResponse> {
  total: number;
  onboarded: number;
}

export function useUsers(input: { search: string; onboarded?: boolean }) {
  return useQuery({
    queryKey: keys.users(input.search, input.onboarded),
    queryFn: () => {
      const params = new URLSearchParams();
      if (input.search) params.set('search', input.search);
      if (input.onboarded !== undefined) params.set('onboarded', String(input.onboarded));
      const query = params.toString();
      return api<UserListResult>(`/admin/users${query ? `?${query}` : ''}`);
    },
  });
}

export function useJourneys(published?: boolean) {
  return useQuery({
    queryKey: keys.journeys(published),
    queryFn: () => {
      const query = published === undefined ? '' : `?published=${published}`;
      return api<Page<JourneyResponse>>(`/admin/journeys${query}`);
    },
  });
}

export function useJourney(id: string) {
  return useQuery({
    queryKey: keys.journey(id),
    queryFn: () => api<JourneyResponse>(`/admin/journeys/${id}`),
  });
}

export function useCreateJourney() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateJourneyBody) =>
      api<JourneyResponse>('/admin/journeys', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['journeys'] });
      invalidateGoalCounts(qc);
    },
  });
}

export function useUpdateJourney(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateJourneyBody) =>
      api<JourneyResponse>(`/admin/journeys/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      invalidateJourney(qc, id);
      // goalKeys may have changed, which moves the counts on the goals screen
      invalidateGoalCounts(qc);
    },
  });
}

export function useDays(journeyId: string, enabled = true) {
  return useQuery({
    queryKey: keys.days(journeyId),
    queryFn: () =>
      api<{ items: JourneyDayResponse[] }>(`/admin/journeys/${journeyId}/days`).then(
        (r) => r.items,
      ),
    enabled,
  });
}

export function usePublishCheck(journeyId: string) {
  return useQuery({
    queryKey: keys.publishCheck(journeyId),
    queryFn: () => api<PublishCheckResponse>(`/admin/journeys/${journeyId}/publish-check`),
  });
}

export function useCreateDay(journeyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateJourneyDayBody) =>
      api<JourneyDayResponse>(`/admin/journeys/${journeyId}/days`, { method: 'POST', body }),
    onSuccess: () => invalidateJourney(qc, journeyId),
  });
}

export function useUpdateDay(journeyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { dayId: string; title: string }) =>
      api<JourneyDayResponse>(`/admin/days/${input.dayId}`, {
        method: 'PATCH',
        body: { title: input.title },
      }),
    onSuccess: () => invalidateJourney(qc, journeyId),
  });
}

export function useReorderDays(journeyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dayIds: string[]) =>
      api<{ items: JourneyDayResponse[] }>(`/admin/journeys/${journeyId}/days/reorder`, {
        method: 'POST',
        body: { dayIds },
      }),
    onSuccess: () => invalidateJourney(qc, journeyId),
  });
}

export function useDeleteDay(journeyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dayId: string) => api(`/admin/days/${dayId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateJourney(qc, journeyId),
  });
}

export function useUpdateStory(journeyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { storyId: string; title?: string; description?: string }) => {
      const { storyId, ...body } = input;
      return api(`/admin/stories/${storyId}`, { method: 'PATCH', body });
    },
    onSuccess: () => invalidateJourney(qc, journeyId),
  });
}

export function useSetPublished(journeyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (published: boolean) =>
      api<JourneyResponse>(`/admin/journeys/${journeyId}/${published ? 'publish' : 'unpublish'}`, {
        method: 'POST',
      }),
    onSuccess: () => {
      invalidateJourney(qc, journeyId);
      void qc.invalidateQueries({ queryKey: ['journeys'] });
      // the Categories screen shows how many of each category's journeys are live
      void qc.invalidateQueries({ queryKey: keys.goals });
    },
  });
}

export function useArchiveJourney(journeyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api(`/admin/journeys/${journeyId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['journeys'] });
      // archived journeys drop out of the counts
      invalidateGoalCounts(qc);
    },
  });
}

/* ── collections: sections → sub-journeys → episodes ─────────────────────────── */

export function useSections(journeyId: string, enabled = true) {
  return useQuery({
    queryKey: keys.sections(journeyId),
    queryFn: () =>
      api<{ items: SectionResponse[] }>(`/admin/journeys/${journeyId}/sections`).then(
        (r) => r.items,
      ),
    enabled,
  });
}

export function useCreateSection(journeyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSectionBody) =>
      api<SectionResponse>(`/admin/journeys/${journeyId}/sections`, { method: 'POST', body }),
    onSuccess: () => invalidateCollection(qc, journeyId),
  });
}

export function useUpdateSection(journeyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sectionId: string } & UpdateSectionBody) => {
      const { sectionId, ...body } = input;
      return api<SectionResponse>(`/admin/sections/${sectionId}`, { method: 'PATCH', body });
    },
    onSuccess: () => invalidateCollection(qc, journeyId),
  });
}

export function useReorderSections(journeyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sectionIds: string[]) =>
      api<{ items: SectionResponse[] }>(`/admin/journeys/${journeyId}/sections/reorder`, {
        method: 'POST',
        body: { sectionIds },
      }),
    onSuccess: () => invalidateCollection(qc, journeyId),
  });
}

export function useDeleteSection(journeyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sectionId: string) => api(`/admin/sections/${sectionId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateCollection(qc, journeyId),
  });
}

/** Every sub-journey of the journey; the editor groups them by section itself. */
export function useSubJourneys(journeyId: string, enabled = true) {
  return useQuery({
    queryKey: keys.subJourneys(journeyId),
    queryFn: () =>
      api<{ items: SubJourneyResponse[] }>(`/admin/journeys/${journeyId}/sub-journeys`).then(
        (r) => r.items,
      ),
    enabled,
  });
}

export function useSubJourney(subJourneyId: string) {
  return useQuery({
    queryKey: keys.subJourney(subJourneyId),
    queryFn: () => api<SubJourneyResponse>(`/admin/sub-journeys/${subJourneyId}`),
  });
}

export function useCreateSubJourney(journeyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSubJourneyBody) =>
      api<SubJourneyResponse>(`/admin/journeys/${journeyId}/sub-journeys`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateCollection(qc, journeyId),
  });
}

export function useUpdateSubJourney(journeyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { subJourneyId: string } & UpdateSubJourneyBody) => {
      const { subJourneyId, ...body } = input;
      return api<SubJourneyResponse>(`/admin/sub-journeys/${subJourneyId}`, {
        method: 'PATCH',
        body,
      });
    },
    onSuccess: (_data, input) => {
      invalidateCollection(qc, journeyId);
      void qc.invalidateQueries({ queryKey: keys.subJourney(input.subJourneyId) });
    },
  });
}

export function useDeleteSubJourney(journeyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (subJourneyId: string) =>
      api(`/admin/sub-journeys/${subJourneyId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateCollection(qc, journeyId),
  });
}

export function useEpisodes(subJourneyId: string) {
  return useQuery({
    queryKey: keys.episodes(subJourneyId),
    queryFn: () =>
      api<{ items: EpisodeResponse[] }>(`/admin/sub-journeys/${subJourneyId}/episodes`).then(
        (r) => r.items,
      ),
  });
}

export function useCreateEpisode(journeyId: string, subJourneyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateEpisodeBody) =>
      api<EpisodeResponse>(`/admin/sub-journeys/${subJourneyId}/episodes`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateEpisodes(qc, journeyId, subJourneyId),
  });
}

export function useUpdateEpisode(journeyId: string, subJourneyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { episodeId: string } & UpdateEpisodeBody) => {
      const { episodeId, ...body } = input;
      return api<EpisodeResponse>(`/admin/episodes/${episodeId}`, { method: 'PATCH', body });
    },
    onSuccess: () => invalidateEpisodes(qc, journeyId, subJourneyId),
  });
}

export function useDeleteEpisode(journeyId: string, subJourneyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (episodeId: string) => api(`/admin/episodes/${episodeId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateEpisodes(qc, journeyId, subJourneyId),
  });
}

/** For the episode form's uploads, which talk to storage directly and finalize against the API. */
export function useInvalidateEpisodes(journeyId: string, subJourneyId: string): () => void {
  const qc = useQueryClient();
  return () => invalidateEpisodes(qc, journeyId, subJourneyId);
}

/** For the section image upload, which talks to storage directly and finalizes against the API. */
export function useInvalidateCollection(journeyId: string): () => void {
  const qc = useQueryClient();
  return () => invalidateCollection(qc, journeyId);
}

/** Sections, sub-journeys, the journey's counts and the publish checklist move together. */
function invalidateCollection(qc: ReturnType<typeof useQueryClient>, journeyId: string): void {
  void qc.invalidateQueries({ queryKey: keys.sections(journeyId) });
  void qc.invalidateQueries({ queryKey: keys.subJourneys(journeyId) });
  invalidateJourney(qc, journeyId);
}

function invalidateEpisodes(
  qc: ReturnType<typeof useQueryClient>,
  journeyId: string,
  subJourneyId: string,
): void {
  void qc.invalidateQueries({ queryKey: keys.episodes(subJourneyId) });
  void qc.invalidateQueries({ queryKey: keys.subJourney(subJourneyId) });
  invalidateCollection(qc, journeyId);
}

/**
 * For writes that do not go through a mutation hook — the signed image uploads, which talk
 * to storage directly and finalize against the API.
 */
export function useInvalidateJourney(id: string): () => void {
  const qc = useQueryClient();
  return () => invalidateJourney(qc, id);
}

function invalidateJourney(qc: ReturnType<typeof useQueryClient>, id: string): void {
  void qc.invalidateQueries({ queryKey: keys.journey(id) });
  void qc.invalidateQueries({ queryKey: keys.days(id) });
  void qc.invalidateQueries({ queryKey: keys.publishCheck(id) });
}

/**
 * A journey's goal tags feed the counts on the goals screen, so anything that creates,
 * retags or archives a journey makes those numbers stale — even though nothing about a
 * goal itself changed. Without this the screen keeps serving cached counts until they
 * age out, which reads as the numbers simply being wrong.
 */
function invalidateGoalCounts(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: keys.goals });
}
